/**
 * ProtectedRoute
 *
 * Wraps a page component. Everything behind it needs a session.
 *
 * On the WEB, a visitor arriving with no session is NOT bounced to the login
 * page. They're given a guest account automatically and dropped straight
 * into the app, because a sign-up form is a poor first impression of a
 * product nobody has seen yet. The nudge to create a real account lives in
 * the sidebar instead (see components/GuestPrompt.jsx).
 *
 * On NATIVE, requested directly: a visitor with no session goes to /welcome
 * (NativeWelcome.jsx) instead of getting a silent guest account — sign up or
 * sign in is required before using the app. This does NOT push a plan/
 * subscription choice; Signup.jsx already sends a native signup straight to
 * /chat with no pricing step (Guideline 3.1.1 — there's no Pricing route on
 * native at all). Upgrading stays a Settings-only, no-CTA read of plan status.
 *
 * On web, only two things still send someone to /login: a registered user
 * whose saved session has genuinely expired (handled in AuthContext), and a
 * guest session the server declined to create.
 *
 * Also enforces the league downgrade/selection flow: if a plan downgrade
 * left the user with more active leagues than their new plan allows
 * (needsLeagueSelection — see context/AuthContext.jsx), normal dashboard/tool
 * pages redirect to /select-leagues instead of rendering. Pass
 * `allowWhileSelectingLeagues` on the few routes that must stay reachable
 * even while that's blocking (the /select-leagues page itself, and the
 * Stripe billing return pages).
 *
 * Usage in App.jsx:
 *   <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 */

import { useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { IS_NATIVE_APP } from '../services/runtime'

function Loading({ label = 'Loading…' }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-gray-800 text-sm">{label}</div>
    </div>
  )
}

export default function ProtectedRoute({ children, allowWhileSelectingLeagues = false }) {
  const { user, loading, planLoading, needsLeagueSelection, startGuestSession, sessionExpired } = useAuth()

  // "Have we already asked for a guest session?" A ref rather than state on
  // purpose: it only guards the effect, and setting state here instead would
  // mean a synchronous setState inside an effect — an extra render pass for a
  // value nothing renders.
  const askedForGuest = useRef(false)

  // The one thing that DOES need a render: the server refused, so the spinner
  // has to become a way forward.
  const [guestFailed, setGuestFailed] = useState(false)

  // A registered user whose saved session expired is NOT a new visitor, and
  // must never be handed a guest account — that would look like their leagues
  // and subscription had vanished. They go to /login below instead.
  const noSession = !loading && !user && !sessionExpired

  useEffect(() => {
    // Native never auto-creates a guest session — see the file header. The
    // render branch below sends a native visitor with no session to /welcome
    // instead, so this effect has nothing to do there.
    if (IS_NATIVE_APP) return
    if (!noSession || askedForGuest.current) return
    askedForGuest.current = true
    startGuestSession().then((created) => {
      // On success `user` arrives through context and this component re-renders
      // with a session; only the failure needs recording.
      if (!created) setGuestFailed(true)
    })
  }, [noSession, startGuestSession])

  // Dev-only: ?preview=1 bypasses auth for UI verification
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview') === '1') {
    return children
  }

  // Still checking localStorage — show nothing yet
  if (loading) {
    return <Loading />
  }

  if (!user) {
    // Their own account is still there — they just need to sign in again.
    if (sessionExpired) {
      return <Navigate to="/login" replace />
    }
    // Native requires a real account — no guest session was ever requested
    // (see the effect above), so straight to the welcome screen's Create
    // account / Sign in choice.
    if (IS_NATIVE_APP) {
      return <Navigate to="/welcome" replace />
    }
    // The server wouldn't start a guest session (rate limited, or down). Sign
    // up is the only way in from here, and it's a far better dead end than a
    // spinner that never resolves.
    if (guestFailed) {
      return <Navigate to="/signup" replace />
    }
    return <Loading label="Setting up JAI…" />
  }

  // Wait for the real needsLeagueSelection value before deciding, so nobody
  // gets flash-redirected to /select-leagues while it's still loading.
  if (!allowWhileSelectingLeagues && !planLoading && needsLeagueSelection) {
    return <Navigate to="/select-leagues" replace />
  }

  // Logged in — render the page
  return children
}
